import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, MessageSquare } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Statuses during which chat is enabled (must match backend allow-list)
const ACTIVE_STATUSES = new Set(["accepted", "in_progress", "arriving", "at_pickup", "en_route"]);

/**
 * RideChat — real-time chat between driver and passenger during a ride.
 * Polls /rides/{ride_id}/chat every 2.5s.
 * Available while ride status ∈ ACTIVE_STATUSES.
 *
 * Props:
 *   rideId      (required)
 *   rideStatus  (optional — if omitted, will use whatever the endpoint returns)
 *   otherName   (optional — for the header label)
 *   variant     "compact" | "full" (default "compact")
 */
export default function RideChat({ rideId, rideStatus, otherName, variant = "compact" }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState(rideStatus || "");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const lastCountRef = useRef(0);

  const active = ACTIVE_STATUSES.has(status);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/rides/${rideId}/chat`);
      setStatus(r.data.ride_status || "");
      const msgs = r.data.messages || [];
      setMessages(msgs);
      // Auto-scroll on new messages
      if (msgs.length > lastCountRef.current) {
        lastCountRef.current = msgs.length;
        setTimeout(() => {
          const el = scrollRef.current?.querySelector?.("[data-radix-scroll-area-viewport]")
            || scrollRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        }, 50);
      }
    } catch (err) {
      if (err.response?.status !== 401) console.error("[RideChat] load failed", err);
    }
  };

  useEffect(() => {
    if (!rideId) return;
    load();
    pollRef.current = setInterval(load, 2500);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rideId]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/rides/${rideId}/chat`, { content: text });
      setMessages((m) => [...m, r.data]);
      setInput("");
    } catch (err) {
      console.error("[RideChat] send failed", err);
    }
    setSending(false);
  };

  if (!rideId) return null;

  const isFull = variant === "full";

  return (
    <Card
      className={`overflow-hidden flex flex-col ${isFull ? "h-full" : ""}`}
      data-testid="ride-chat"
    >
      <div className="flex items-center gap-2 p-3 border-b border-border bg-muted/40">
        <MessageSquare className="w-4 h-4 text-primary" />
        <div className="text-sm font-semibold flex-1 truncate">
          Chat da corrida{otherName ? ` — ${otherName}` : ""}
        </div>
        {!active && (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {status === "completed" ? "encerrado" : status === "canceled" ? "cancelado" : "indisponível"}
          </span>
        )}
      </div>

      <ScrollArea ref={scrollRef} className={`px-3 ${isFull ? "flex-1" : "h-56"}`}>
        <div className="py-3 space-y-2" data-testid="ride-chat-messages">
          {messages.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-6">
              {active
                ? "Sem mensagens ainda. Diga um oi 👋"
                : "Chat disponível apenas durante a corrida ativa."}
            </div>
          )}
          {messages.map((m) => {
            const mine = m.from_user_id === user?.user_id;
            return (
              <div
                key={m.msg_id}
                data-testid={`ride-chat-msg-${m.msg_id}`}
                className={`flex items-end gap-2 ${mine ? "flex-row-reverse" : ""}`}
              >
                <Avatar className="w-6 h-6 flex-shrink-0">
                  <AvatarImage src={m.from_picture} />
                  <AvatarFallback>{m.from_name?.[0]}</AvatarFallback>
                </Avatar>
                <div className={`max-w-[75%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
                  <div
                    className={`text-sm px-3 py-2 rounded-2xl break-words ${
                      mine
                        ? "bg-primary text-primary-foreground rounded-br-sm"
                        : "bg-muted rounded-bl-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 px-1">
                    {m.role === "driver" ? "🚗 " : "🙋 "}
                    {new Date(m.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {active ? (
        <div className="p-2 border-t border-border flex gap-2">
          <Input
            data-testid="ride-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Escreva uma mensagem..."
            maxLength={500}
          />
          <Button
            data-testid="ride-chat-send"
            size="icon"
            onClick={send}
            disabled={sending || !input.trim()}
            className="rounded-full flex-shrink-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="p-2 border-t border-border text-[11px] text-muted-foreground text-center">
          {status === "completed"
            ? "Corrida encerrada — histórico do chat disponível apenas por 24h."
            : "O chat será liberado quando a corrida for confirmada."}
        </div>
      )}
    </Card>
  );
}
